// QRApi.js - API utilities for QR operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for QR API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_qr',
        `QR API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[QRApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_qr',
      `QR API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[QRApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for QR Management.
 */
export const qrApi = {
  /**
   * Fetches all QR codes.
   * GET /QR/getall
   * @param {Object} [options={}] - Optional parameters for pagination.
   * @param {string} [options.lastEvaluatedKey] - The last evaluated key for pagination.
   * @returns {Promise<Object>} - Response object containing:
   *   - items: Array of QR code objects
   *   - count: Total number of items
   *   - last_evaluated_key: Key for next page (null if no more pages)
   *   - has_more: Boolean indicating if there are more results
   * @throws {Error} - Throws an error if the API request fails.
   */
  getAll: async (options = {}) => {
    try {
      console.log('QR API: Fetching all QR codes', options);

      // Build query parameters if pagination is needed
      let endpoint = '/QR/getall';
      const queryParams = [];
      
      // Support both last_key (new API) and lastEvaluatedKey (legacy)
      const lastKey = options.last_key || options.lastEvaluatedKey;
      if (lastKey) {
        queryParams.push(`last_key=${encodeURIComponent(lastKey)}`);
      }
      
      // Support limit parameter
      if (options.limit) {
        queryParams.push(`limit=${options.limit}`);
      }
      
      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`;
      }

      const response = await makeApiRequest(endpoint);
      const responseData = await response.json();

      console.log('QR API: Response status:', response.status);
      console.log('QR API: Response data:', responseData);

      if (response.status === 200) {
        // Validate response structure
        if (responseData && typeof responseData === 'object') {
          // Ensure the response has the expected structure
          // Support both last_key (new API) and last_evaluated_key (legacy)
          const lastKeyValue = responseData.last_key || responseData.last_evaluated_key || null;
          
          const formattedResponse = {
            items: Array.isArray(responseData.items) ? responseData.items : [],
            count: responseData.count || 0,
            last_key: lastKeyValue,
            last_evaluated_key: lastKeyValue, // Keep for backward compatibility
            has_more: responseData.has_more || false,
          };
          
          // Log API event for successful fetch
          loggingService.logInfo(
            'api_success_qr',
            `Successfully fetched QR codes`,
            {
              endpoint: '/QR/getall',
              method: 'GET',
              count: formattedResponse.count,
              hasMore: formattedResponse.has_more,
            }
          ).catch(logError => {
            console.warn('[QRApi] Failed to log success event:', logError);
          });
          
          return formattedResponse;
        } else {
          throw new Error('Invalid response format from QR API');
        }
      } else {
        throw new Error(
          `Failed to fetch QR codes: ${response.status} - ${JSON.stringify(responseData)}`
        );
      }
    } catch (error) {
      console.error('QR API: Error in getAll:', error);

      // Handle specific error cases based on status codes
      if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 403) {
        throw new Error('You do not have permission to access QR codes.');
      } else {
        throw new Error(`Error fetching QR codes: ${error.message}`);
      }
    }
  },

  /**
   * Fetches all QR codes and returns only the items array.
   * This is a convenience method that extracts items from the paginated response.
   * GET /QR/getall
   * @returns {Promise<Array>} - Array of QR code objects.
   * @throws {Error} - Throws an error if the API request fails.
   */
  getAllItems: async () => {
    try {
      const response = await qrApi.getAll();
      return response.items || [];
    } catch (error) {
      console.error('QR API: Error in getAllItems:', error);
      throw error;
    }
  },

  /**
   * Fetches a single QR code by id (from paginated list).
   * @param {string} qrId
   * @returns {Promise<Object>}
   */
  getById: async qrId => {
    if (!qrId) {
      throw new Error('QR id is required');
    }

    let lastKey = null;
    let hasMore = true;

    while (hasMore) {
      const options = lastKey ? { last_key: lastKey, limit: 100 } : { limit: 100 };
      const response = await qrApi.getAll(options);
      const match = (response.items || []).find(item => item.qr_id === qrId);
      if (match) return match;

      hasMore = response.has_more || false;
      lastKey = response.last_key || response.last_evaluated_key || null;
      if (hasMore && !lastKey) break;
    }

    throw new Error('QR code not found');
  },

  /**
   * Creates a QR code.
   * POST /QR/create
   * @param {Object} data - { name, landmark, location?, site_id? }
   */
  create: async data => {
    const response = await makeApiRequest('/QR/create', 'POST', data);
    return response.json().catch(() => ({}));
  },

  /**
   * Updates a QR code.
   * PUT /QR/update
   * @param {string} qrId
   * @param {Object} data
   */
  update: async (qrId, data) => {
    const response = await makeApiRequest('/QR/update', 'PUT', {
      qr_id: qrId,
      ...data,
    });
    return response.json().catch(() => ({}));
  },

  /**
   * Deletes a QR code.
   * DELETE /QR/delete
   * @param {string} qrId
   */
  delete: async qrId => {
    await makeApiRequest(`/QR/delete?qr_id=${encodeURIComponent(qrId)}`, 'DELETE');
  },
};

